// Platform wrapper around the shared capture record state machine
// (@twasta/page-runtime/txn/capture useEmbedRecords). The shared hook takes
// an injected EmbedDataApi + notifier (the package carries no axios / toast
// of its own); this wrapper curries in the platform's embedDataApi adapter
// + react-hot-toast so every existing call site keeps its original
// signature and behavior exactly.

import toast from 'react-hot-toast'
import {
  useEmbedRecords as useEmbedRecordsCore,
  type UseEmbedRecordsOpts as CoreUseEmbedRecordsOpts,
  type UseEmbedRecordsResult,
} from '@twasta/page-runtime/txn/capture'
import type { TxnNotifier } from '@twasta/page-runtime/txn'
import { designerEmbedDataApi } from './captureRuntime'
import { livePreviewRetrieve } from './api'

export type UseEmbedRecordsOpts = Omit<CoreUseEmbedRecordsOpts, 'api' | 'notify'>

// Same toast shapes the hook used before the move (info = the ℹ️ toast the
// severity-'P' save prompts rendered).
const toastNotify: TxnNotifier = {
  success: (m) => { toast.success(m) },
  error: (m) => { toast.error(m) },
  info: (m) => { toast(m, { icon: 'ℹ️' }) },
  warn: (m) => { toast(m, { icon: '⚠️' }) },
}

export function useEmbedRecords(opts: UseEmbedRecordsOpts): UseEmbedRecordsResult {
  return useEmbedRecordsCore({
    ...opts,
    api: designerEmbedDataApi,
    notify: toastNotify,
  })
}

// The state machine (types + doc-state bridge + buffer helpers) moved
// verbatim into the shared package — re-export so existing importers keep
// working. EmbedDocStateContext must stay the package's own context object
// (the shared hook reads it), so it is re-exported, never re-created.
export type {
  EmbedRecord, EmbedDocState, ResolvedFilters, PageInputValues,
  AuthoredFilter, AuthoredArg, UseEmbedRecordsResult,
} from '@twasta/page-runtime/txn/capture'
export {
  EmbedDocStateContext, annotateRowError, markChildrenSaved,
} from '@twasta/page-runtime/txn/capture'

// Re-export the retrieve-by-id helper for callers that want to drop a
// specific previously-saved record back into the editor without doing a
// full list refresh.
export { livePreviewRetrieve }
